// ---------------------------------------------------------------------------
// BIL-017 / F-110 — Stripe Connect CQRS event catalog + pure builders
// ---------------------------------------------------------------------------
// Per CLAUDE.md Governance Rule #6 (atomic events — one fact per event) and
// Rule #7 (event catalog before implementation), this file pre-catalogues
// every Stripe-Connect domain event the Phase-3 plugin
// (`packages/payments/src/plugins/stripe-connect.ts`) + the downstream
// payout cron will emit into `customer_events`.
//
// Five event types — one business fact each. No compound events.
//
//   billing.connect_account_created@v1
//     Persistent record of a newly created connected account. Fires once
//     per (tenant_id, stripe_account_id) — the writer enforces idempotency
//     via the unique index on `stripe_connect_accounts.stripe_account_id`.
//
//   billing.connect_account_status_changed@v1
//     Capability-flag transition (charges_enabled / payouts_enabled /
//     details_submitted) OR requirements vector change. Fires whenever
//     `getConnectAccountStatus` or the `account.updated` webhook detects
//     a delta. Carries before/after for audit.
//
//   billing.connect_transfer_initiated@v1
//     Money-movement intent. Emitted BEFORE the Stripe `POST /v1/transfers`
//     call so a crash mid-flight leaves an idempotent trail. Re-runs use
//     the same `idempotency_key` so Stripe returns the same transfer id.
//
//   billing.connect_payout_completed@v1
//     Downstream `payout.paid` webhook from Stripe — money has actually
//     landed in the connected account's bank. NOT emitted by `createTransfer`;
//     only by `handleConnectWebhook`.
//
//   billing.connect_payout_failed@v1
//     Downstream `payout.failed` webhook from Stripe — bank rejected the
//     payout. Carries Stripe's `failure_code` + `failure_message` so the
//     operator dashboard can surface a re-action (re-KYC, update bank).
//
// Pure builders only. No I/O. No DB writes. No env access. The writer
// (operator route or webhook ingestor) owns actual emission to
// `customer_events` + the `event-router` worker — mirrors the AN-001 /
// MKT-001 / BIL-017-payout Phase 3 pattern.
// ---------------------------------------------------------------------------

import { z } from "zod";

// ---------------------------------------------------------------------------
// Event type catalog — frozen string constants
// ---------------------------------------------------------------------------

export const BIL_017_CONNECT_ACCOUNT_CREATED_EVENT =
  "billing.connect_account_created@v1" as const;
export const BIL_017_CONNECT_ACCOUNT_STATUS_CHANGED_EVENT =
  "billing.connect_account_status_changed@v1" as const;
export const BIL_017_CONNECT_TRANSFER_INITIATED_EVENT =
  "billing.connect_transfer_initiated@v1" as const;
export const BIL_017_CONNECT_PAYOUT_COMPLETED_EVENT =
  "billing.connect_payout_completed@v1" as const;
export const BIL_017_CONNECT_PAYOUT_FAILED_EVENT =
  "billing.connect_payout_failed@v1" as const;

export const BIL_017_CONNECT_EVENTS = [
  BIL_017_CONNECT_ACCOUNT_CREATED_EVENT,
  BIL_017_CONNECT_ACCOUNT_STATUS_CHANGED_EVENT,
  BIL_017_CONNECT_TRANSFER_INITIATED_EVENT,
  BIL_017_CONNECT_PAYOUT_COMPLETED_EVENT,
  BIL_017_CONNECT_PAYOUT_FAILED_EVENT,
] as const;

export type Bil017ConnectEventType = (typeof BIL_017_CONNECT_EVENTS)[number];

export function isBil017ConnectEventType(
  value: unknown,
): value is Bil017ConnectEventType {
  return (
    typeof value === "string" &&
    (BIL_017_CONNECT_EVENTS as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Vocabulary enums — closed sets so consumers `switch` exhaustively
// ---------------------------------------------------------------------------

export const BIL_017_CONNECT_ACCOUNT_TYPES = [
  "express",
  "standard",
  "custom",
] as const;
export type Bil017ConnectAccountType =
  (typeof BIL_017_CONNECT_ACCOUNT_TYPES)[number];

export const BIL_017_CONNECT_STATUS_CHANGE_REASONS = [
  "webhook_account_updated",
  "manual_status_sync",
  "onboarding_link_completed",
] as const;
export type Bil017ConnectStatusChangeReason =
  (typeof BIL_017_CONNECT_STATUS_CHANGE_REASONS)[number];

// ---------------------------------------------------------------------------
// Shared base — every event carries these
// ---------------------------------------------------------------------------

export interface Bil017ConnectEventBase {
  tenantId: string;
  occurredAtIso: string; // ISO-8601 timestamp
  correlationId: string; // derived per-event — see deriveCorrelationId()
}

// ---------------------------------------------------------------------------
// Per-event payload schemas (Zod) + TypeScript types
// ---------------------------------------------------------------------------

export const Bil017ConnectAccountCreatedPayloadSchema = z.object({
  type: z.literal(BIL_017_CONNECT_ACCOUNT_CREATED_EVENT),
  tenantId: z.string().uuid(),
  stripeAccountId: z.string().min(1),
  accountType: z.enum(BIL_017_CONNECT_ACCOUNT_TYPES),
  country: z.string().length(2),
  defaultCurrency: z.string().min(1),
  email: z.string().email().nullable(),
  businessName: z.string().nullable(),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type Bil017ConnectAccountCreatedPayload = z.infer<
  typeof Bil017ConnectAccountCreatedPayloadSchema
>;

export const Bil017ConnectAccountStatusChangedPayloadSchema = z.object({
  type: z.literal(BIL_017_CONNECT_ACCOUNT_STATUS_CHANGED_EVENT),
  tenantId: z.string().uuid(),
  stripeAccountId: z.string().min(1),
  reason: z.enum(BIL_017_CONNECT_STATUS_CHANGE_REASONS),
  before: z.object({
    chargesEnabled: z.boolean(),
    payoutsEnabled: z.boolean(),
    detailsSubmitted: z.boolean(),
    requirementsDisabledReason: z.string().nullable(),
  }),
  after: z.object({
    chargesEnabled: z.boolean(),
    payoutsEnabled: z.boolean(),
    detailsSubmitted: z.boolean(),
    requirementsDisabledReason: z.string().nullable(),
    requirementsCurrentlyDue: z.array(z.string()),
  }),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type Bil017ConnectAccountStatusChangedPayload = z.infer<
  typeof Bil017ConnectAccountStatusChangedPayloadSchema
>;

export const Bil017ConnectTransferInitiatedPayloadSchema = z.object({
  type: z.literal(BIL_017_CONNECT_TRANSFER_INITIATED_EVENT),
  tenantId: z.string().uuid(),
  destinationAccountId: z.string().min(1),
  amountMinor: z.number().int().positive(),
  currency: z.string().min(1),
  transferGroup: z.string().nullable(),
  sourceTransaction: z.string().nullable(),
  idempotencyKey: z.string().min(1),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type Bil017ConnectTransferInitiatedPayload = z.infer<
  typeof Bil017ConnectTransferInitiatedPayloadSchema
>;

export const Bil017ConnectPayoutCompletedPayloadSchema = z.object({
  type: z.literal(BIL_017_CONNECT_PAYOUT_COMPLETED_EVENT),
  tenantId: z.string().uuid(),
  stripeAccountId: z.string().min(1),
  payoutId: z.string().min(1),
  amountMinor: z.number().int().positive(),
  currency: z.string().min(1),
  arrivalDateIso: z.string().datetime({ offset: true }).nullable(),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type Bil017ConnectPayoutCompletedPayload = z.infer<
  typeof Bil017ConnectPayoutCompletedPayloadSchema
>;

export const Bil017ConnectPayoutFailedPayloadSchema = z.object({
  type: z.literal(BIL_017_CONNECT_PAYOUT_FAILED_EVENT),
  tenantId: z.string().uuid(),
  stripeAccountId: z.string().min(1),
  payoutId: z.string().min(1),
  amountMinor: z.number().int().positive(),
  currency: z.string().min(1),
  failureCode: z.string().min(1),
  failureMessage: z.string().min(1).max(1024),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type Bil017ConnectPayoutFailedPayload = z.infer<
  typeof Bil017ConnectPayoutFailedPayloadSchema
>;

// ---------------------------------------------------------------------------
// Discriminated union — consumers `switch (event.type)` for exhaustive narrow
// ---------------------------------------------------------------------------

export type Bil017ConnectEventEnvelope =
  | Bil017ConnectAccountCreatedPayload
  | Bil017ConnectAccountStatusChangedPayload
  | Bil017ConnectTransferInitiatedPayload
  | Bil017ConnectPayoutCompletedPayload
  | Bil017ConnectPayoutFailedPayload;

// ---------------------------------------------------------------------------
// Correlation-id derivation
// ---------------------------------------------------------------------------
// Each event type pins its own deterministic correlation root so the
// reconciler can join across emit, status-sync, transfer-initiate,
// payout.paid, and payout.failed for the same tenant + account.
//
//   account_created           → `connect:{stripeAccountId}`
//   account_status_changed    → `connect:{stripeAccountId}`
//   transfer_initiated        → `connect-transfer:{idempotencyKey}`
//   payout_completed/failed   → `connect-payout:{payoutId}`

export function deriveConnectAccountCorrelationId(
  stripeAccountId: string,
): string {
  if (!stripeAccountId) {
    throw new Error("deriveConnectAccountCorrelationId: stripeAccountId required");
  }
  return `connect:${stripeAccountId}`;
}

export function deriveConnectTransferCorrelationId(
  idempotencyKey: string,
): string {
  if (!idempotencyKey) {
    throw new Error("deriveConnectTransferCorrelationId: idempotencyKey required");
  }
  return `connect-transfer:${idempotencyKey}`;
}

export function deriveConnectPayoutCorrelationId(payoutId: string): string {
  if (!payoutId) {
    throw new Error("deriveConnectPayoutCorrelationId: payoutId required");
  }
  return `connect-payout:${payoutId}`;
}

// ---------------------------------------------------------------------------
// Pure builders — accept caller primitives, return typed envelope
// ---------------------------------------------------------------------------

function assertNonEmpty(value: string, field: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`BIL-017 Connect emitter: ${field} must be non-empty string`);
  }
  return value;
}

function assertPositiveInteger(value: number, field: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 1) {
    throw new Error(
      `BIL-017 Connect emitter: ${field} must be positive integer (got ${value})`,
    );
  }
  return value;
}

function nowIso(now: Date | undefined): string {
  return (now ?? new Date()).toISOString();
}

export interface BuildConnectAccountCreatedInput {
  tenantId: string;
  stripeAccountId: string;
  accountType: Bil017ConnectAccountType;
  country: string;
  defaultCurrency: string;
  email: string | null;
  businessName: string | null;
  now?: Date;
}

export function buildConnectAccountCreatedEvent(
  input: BuildConnectAccountCreatedInput,
): Bil017ConnectAccountCreatedPayload {
  return Bil017ConnectAccountCreatedPayloadSchema.parse({
    type: BIL_017_CONNECT_ACCOUNT_CREATED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    stripeAccountId: assertNonEmpty(input.stripeAccountId, "stripeAccountId"),
    accountType: input.accountType,
    country: assertNonEmpty(input.country, "country").toUpperCase(),
    defaultCurrency: assertNonEmpty(
      input.defaultCurrency,
      "defaultCurrency",
    ).toLowerCase(),
    email: input.email,
    businessName: input.businessName,
    occurredAtIso: nowIso(input.now),
    correlationId: deriveConnectAccountCorrelationId(input.stripeAccountId),
  });
}

export interface BuildConnectAccountStatusChangedInput {
  tenantId: string;
  stripeAccountId: string;
  reason: Bil017ConnectStatusChangeReason;
  before: {
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirementsDisabledReason: string | null;
  };
  after: {
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirementsDisabledReason: string | null;
    requirementsCurrentlyDue: string[];
  };
  now?: Date;
}

export function buildConnectAccountStatusChangedEvent(
  input: BuildConnectAccountStatusChangedInput,
): Bil017ConnectAccountStatusChangedPayload {
  return Bil017ConnectAccountStatusChangedPayloadSchema.parse({
    type: BIL_017_CONNECT_ACCOUNT_STATUS_CHANGED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    stripeAccountId: assertNonEmpty(input.stripeAccountId, "stripeAccountId"),
    reason: input.reason,
    before: input.before,
    after: input.after,
    occurredAtIso: nowIso(input.now),
    correlationId: deriveConnectAccountCorrelationId(input.stripeAccountId),
  });
}

export interface BuildConnectTransferInitiatedInput {
  tenantId: string;
  destinationAccountId: string;
  amountMinor: number;
  currency: string;
  transferGroup: string | null;
  sourceTransaction: string | null;
  idempotencyKey: string;
  now?: Date;
}

export function buildConnectTransferInitiatedEvent(
  input: BuildConnectTransferInitiatedInput,
): Bil017ConnectTransferInitiatedPayload {
  return Bil017ConnectTransferInitiatedPayloadSchema.parse({
    type: BIL_017_CONNECT_TRANSFER_INITIATED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    destinationAccountId: assertNonEmpty(
      input.destinationAccountId,
      "destinationAccountId",
    ),
    amountMinor: assertPositiveInteger(input.amountMinor, "amountMinor"),
    currency: assertNonEmpty(input.currency, "currency").toLowerCase(),
    transferGroup: input.transferGroup,
    sourceTransaction: input.sourceTransaction,
    idempotencyKey: assertNonEmpty(input.idempotencyKey, "idempotencyKey"),
    occurredAtIso: nowIso(input.now),
    correlationId: deriveConnectTransferCorrelationId(input.idempotencyKey),
  });
}

export interface BuildConnectPayoutCompletedInput {
  tenantId: string;
  stripeAccountId: string;
  payoutId: string;
  amountMinor: number;
  currency: string;
  arrivalDateIso: string | null;
  now?: Date;
}

export function buildConnectPayoutCompletedEvent(
  input: BuildConnectPayoutCompletedInput,
): Bil017ConnectPayoutCompletedPayload {
  return Bil017ConnectPayoutCompletedPayloadSchema.parse({
    type: BIL_017_CONNECT_PAYOUT_COMPLETED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    stripeAccountId: assertNonEmpty(input.stripeAccountId, "stripeAccountId"),
    payoutId: assertNonEmpty(input.payoutId, "payoutId"),
    amountMinor: assertPositiveInteger(input.amountMinor, "amountMinor"),
    currency: assertNonEmpty(input.currency, "currency").toLowerCase(),
    arrivalDateIso: input.arrivalDateIso,
    occurredAtIso: nowIso(input.now),
    correlationId: deriveConnectPayoutCorrelationId(input.payoutId),
  });
}

export interface BuildConnectPayoutFailedInput {
  tenantId: string;
  stripeAccountId: string;
  payoutId: string;
  amountMinor: number;
  currency: string;
  failureCode: string;
  failureMessage: string;
  now?: Date;
}

export const BIL_017_CONNECT_FAILURE_MESSAGE_MAX_LENGTH = 1024;

export function buildConnectPayoutFailedEvent(
  input: BuildConnectPayoutFailedInput,
): Bil017ConnectPayoutFailedPayload {
  const trimmedMsg = (input.failureMessage ?? "").slice(
    0,
    BIL_017_CONNECT_FAILURE_MESSAGE_MAX_LENGTH,
  );
  return Bil017ConnectPayoutFailedPayloadSchema.parse({
    type: BIL_017_CONNECT_PAYOUT_FAILED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    stripeAccountId: assertNonEmpty(input.stripeAccountId, "stripeAccountId"),
    payoutId: assertNonEmpty(input.payoutId, "payoutId"),
    amountMinor: assertPositiveInteger(input.amountMinor, "amountMinor"),
    currency: assertNonEmpty(input.currency, "currency").toLowerCase(),
    failureCode: assertNonEmpty(input.failureCode, "failureCode"),
    failureMessage: assertNonEmpty(trimmedMsg, "failureMessage"),
    occurredAtIso: nowIso(input.now),
    correlationId: deriveConnectPayoutCorrelationId(input.payoutId),
  });
}
